import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

import { ampli, ApiKey, DefaultConfiguration } from "./base/index";

/** Initialize the Amplitude (Ampli) client once at app startup.
 *
 *  `ApiKey.default` is baked at codegen time (the `studio-web` source the
 *  tracking plan was pulled against). Deployments override the destination
 *  via `VITE_AMPLITUDE_API_KEY` so events can land in a different Amplitude
 *  project; locally we fall back to the baked key. When no key resolves the
 *  Ampli client never loads and every `track*` call is a guarded no-op.
 *
 *  Ingest uses the SDK's default Amplitude endpoint (no same-origin proxy). */
export function initAmpli(): void {
  // Idempotency guard. initExternalIntegrations runs once at module scope, but a
  // second call (dev HMR, a test) must not create a second instance with its own
  // Session Replay recorder behind ampli's back — ampli.load's own guard fires
  // only after that instance is already built and recording.
  if (ampli.isLoaded) return;

  const override = window.__env__?.VITE_AMPLITUDE_API_KEY;
  const apiKey = override && override.length > 0 ? override : ApiKey.default;
  if (!apiKey) return;

  // Session Replay must record against the same Amplitude instance that emits
  // events — replays link to sessions by session id. Ampli's `apiKey` path
  // creates and inits its own private instance (base/index.ts), leaving nowhere
  // to attach the plugin, so we own the instance instead: add the plugin, init,
  // and hand it to ampli.load via `client.instance`. That path skips init, so we
  // apply DefaultConfiguration here exactly as the `apiKey` path would have.
  const instance = amplitude.createInstance();
  instance.add(sessionReplayPlugin({ sampleRate: 1 }));
  instance.init(apiKey, undefined, { ...DefaultConfiguration });

  ampli.load({ client: { instance } });
}

export * from "./base/index";
